import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMapPin, FiClock, FiCheckCircle, FiTool, FiDollarSign, FiShield } from 'react-icons/fi';

const DallasBMWRepair = () => {
  const bmwModels = [
    '1 Series', '2 Series', '3 Series', '4 Series', '5 Series', '6 Series', '7 Series', '8 Series',
    'X1', 'X2', 'X3', 'X4', 'X5', 'X6', 'X7', 'Z4', 'i3', 'i4', 'i8', 'iX',
    'M2', 'M3', 'M4', 'M5', 'M6', 'M8', 'X3 M', 'X4 M', 'X5 M', 'X6 M'
  ];

  const dallasNeighborhoods = [
    'Highland Park', 'University Park', 'Preston Hollow', 'Uptown Dallas',
    'North Dallas', 'Lakewood', 'Knox-Henderson', 'Downtown Dallas',
    'Deep Ellum', 'Bishop Arts', 'Design District', 'Victory Park'
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen"
    >
      <Helmet>
        <title>BMW Repair Dallas | Expert BMW Service & Maintenance | Paradise-Performance</title>
        <meta name="description" content="BMW repair Dallas specialists. Factory-trained technicians, genuine BMW parts, same-day service. 30% less than Dallas BMW dealerships. Serving Highland Park, Plano, Richardson. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/dallas-bmw-repair" />
        
        {/* Local Business Schema for BMW Repair */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance - BMW Repair Dallas",
            "description": "Expert BMW repair and service in Dallas, TX area",
            "url": "https://paradise-performance.com/dallas-bmw-repair",
            "telephone": "(817) 382-2316",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "116 W Garza Ave",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": {
              "@type": "City",
              "name": "Dallas",
              "sameAs": "https://www.wikidata.org/wiki/Q16557"
            },
            "brand": {
              "@type": "Brand",
              "name": "BMW",
              "sameAs": "https://www.wikidata.org/wiki/Q26678"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "324",
              "bestRating": "5",
              "worstRating": "1"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "BMW Repair Services Dallas",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "BMW Oil Service Dallas",
                    "description": "BMW oil change and inspection service"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "BMW Brake Repair Dallas",
                    "description": "BMW brake pad and rotor replacement"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "BMW Engine Diagnostics Dallas",
                    "description": "BMW check engine light diagnosis and repair"
                  }
                }
              ]
            },
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "08:00",
                "closes": "18:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Saturday",
                "opens": "09:00",
                "closes": "15:00"
              }
            ]
          })}
        </script>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does BMW repair cost in Dallas?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "BMW repair costs in Dallas vary by service. At Paradise-Performance, we offer BMW repairs at 30% less than Dallas dealerships. Oil changes start at $129, brake service from $350, and major repairs with transparent pricing."
                }
              },
              {
                "@type": "Question",
                "name": "Where is the best BMW repair shop near Dallas?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Paradise-Performance is the top-rated BMW repair shop serving Dallas, located only 60 minutes north via US-380. We offer expert techniians, genuine parts, and same-day service for most repairs."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section - BMW Dallas Focus */}
      <section 
        className="relative py-24 bg-gradient-to-br from-gray-900 to-blue-900 text-white"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-5xl">
            <motion.h1 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-6xl font-bold mb-6 text-white"
            >
              BMW Repair Dallas
              <span className="block text-2xl md:text-3xl mt-4 font-normal">
                Expert BMW Service for the Dallas Metroplex
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-xl mb-8 text-gray-200"
            >
              Factory-trained BMW specialists serving Highland Park, North Dallas, 
              Plano, Richardson and all of DFW.
            </motion.p>

            {/* Value Props */}
            <motion.div 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
            >
              <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-white">
                <FiDollarSign className="text-2xl mb-2 text-green-400" />
                <h3 className="font-bold">30% Less Than Dealerships</h3>
                <p className="text-sm">Save vs Dallas BMW dealers</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-white">
                <FiTool className="text-2xl mb-2 text-blue-400" />
                <h3 className="font-bold">Same-Day Service</h3>
                <p className="text-sm">Most repairs completed today</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-white">
                <FiShield className="text-2xl mb-2 text-yellow-400" />
                <h3 className="font-bold">1 Year Warranty</h3>
                <p className="text-sm">12,000 mile guarantee</p>
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href="tel:9403490038"
                className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-lg transition-colors"
              >
                <FiPhone className="mr-2" />
                Call Now: (817) 382-2316
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
              >
                Get Free Quote
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dallas BMW Owners Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Trusted by BMW Owners Throughout Dallas
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 max-w-5xl mx-auto mb-12">
            {dallasNeighborhoods.map((area, index) => (
              <motion.div
                key={area}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
                className="bg-gray-50 rounded-lg p-3 text-center text-sm hover:bg-blue-50 transition-colors"
              >
                {area}
              </motion.div>
            ))}
          </div>

          <div className="bg-blue-50 rounded-lg p-8 max-w-4xl mx-auto">
            <h3 className="text-xl font-bold mb-4 text-center">
              Why Dallas BMW Drivers Choose Paradise-Performance
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <FiCheckCircle className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <span><strong>Closer than you think:</strong> Just 40 minutes from North Dallas</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <span><strong>Dallas shuttle service:</strong> Drop off and we'll get you back</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <span><strong>Loaner vehicles:</strong> Stay mobile while we service your BMW</span>
                </li>
              </ul>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <FiCheckCircle className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <span><strong>BMW-expert techs:</strong> Factory trainning, not just general mechanics</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <span><strong>Genuine BMW parts:</strong> In stock for faster service</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <span><strong>Latest BMW software:</strong> ISTA diagnostics & programming</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BMW Models We Service */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">
            All BMW Models Serviced for Dallas Area
          </h2>
          <p className="text-center text-gray-600 mb-12">
            From classic 3 Series to the latest electric models, we service every BMW on Dallas roads
          </p>
          
          <div className="flex flex-wrap justify-center gap-2 max-w-5xl mx-auto">
            {bmwModels.map((model, index) => (
              <motion.span
                key={model}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: index * 0.02 }}
                className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700 shadow-sm hover:shadow-md hover:bg-blue-50 transition-all"
              >
                BMW {model}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* Common BMW Repairs for Dallas */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Common BMW Repairs We Handle for Dallas Customers
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 text-blue-600">Regular Maintenance</h3>
              <ul className="space-y-2">
                <li className="flex justify-between">
                  <span className="text-gray-600">from $129</span>
                </li>
                <li className="flex justify-between">
                  <span>Brake Fluid Flush</span>
                  <span className="text-gray-600">from $149</span>
                </li>
                <li className="flex justify-between">
                  <span>Cabin Filter</span>
                  <span className="text-gray-600">from $89</span>
                </li>
                <li className="flex justify-between">
                  <span>Spark Plugs</span>
                  <span className="text-gray-600">from $249</span>
                </li>
                <li className="flex justify-between">
                  <span>Transmission Service</span>
                  <span className="text-gray-600">from $399</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 text-blue-600">Common Repairs</h3>
              <ul className="space-y-2">
                <li>• Valve Cover Gasket Leaks</li>
                <li>• Oil Filter Housing Gasket</li>
                <li>• Cooling System Issues</li>
                <li>• Window Regulators</li>
                <li>• VANOS System Repairs</li>
                <li>• Turbocharger Service</li>
                <li>• Electronic Water Pumps</li>
                <li>• Fuel Pump Replacement</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4 text-blue-600">Specialty Services</h3>
              <ul className="space-y-2">
                <li>• M Performance Tuning</li>
                <li>• Coding & Programming</li>
                <li>• Retrofits & Upgrades</li>
                <li>• Track Day Prep</li>
                <li>• Ceramic Coating</li>
                <li>• PPF Installation</li>
                <li>• Wheel Alignment</li>
                <li>• AC Recharge & Repair</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Dallas-Specific SEO Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2>BMW Repair Shop Serving Dallas-Fort Worth</h2>
            <p>
              When your BMW needs service in Dallas, you have choices. You could go to the BMW dealership 
              on Lemmon Avenue or Central Expressway and pay premium prices. Or you could trust your 
              Ultimate Driving Machine to Paradise-Performance, where Dallas BMW owners have been coming 
              for expert service at fair prices ever since their first visit.
            </p>
            
            <h3>Dallas to Paradise-Performance - An Easy Drive</h3>
            <p>
              Located just 60 minutes north of Dallas via US-380, Paradise-Performance is easily 
              accessible from anywhere in the Dallas metro. Whether you're coming from Highland Park, 
              North Dallas, or Downtown, the drive to Paradise is worth it for the exceptional service 
              and significant savings compared to Dallas BMW dealerships.
            </p>
            
            <h3>Real Savings for Dallas BMW Owners</h3>
            <p>
              Dallas BMW dealerships charge premium rates - often $200+ per hour for labor. At Paradise 
              Performance, we provide the same factory-level service using genuine BMW parts, but at 
              rates 30-40% lower. For a typical BMW 5 Series brake job that might cost $1,400 at a 
              Dallas dealer, our customers pay around $950 - that's $450 in savings that stays in 
              your pocket.
            </p>
            
            <h3>Why Dallas BMW Enthusiasts Trust Us</h3>
            <p>
              The Dallas area has one of the highest concentrations of BMW vehicles in Texas. From the 
              business professionals in Uptown driving new 7 Series to the enthusiasts in Plano with 
              modified M3s, we service them all. Our technicians are BMW-professional and use the same 
              ISTA diagnostic systems found at Dallas BMW dealerships.
            </p>
            
            <h3>Convenient for North Dallas and Beyond</h3>
            <p>
              Paradise-Performance is particularly convenient for North Dallas residents in areas like 
              Preston Hollow, Richardson, Plano, Frisco, and Allen. Many of our Dallas customers 
              schedule early morning drop-offs and use our shuttle service or loaner vehicles to 
              continue their day without interruption.
            </p>
          </div>
        </div>
      </section>

      {/* Reviews/Testimonials */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            What Dallas BMW Owners Say About Us
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex mb-3">
                {'★★★★★'.split('').map((star, i) => (
                  <span key={i} className="text-yellow-400 text-xl">{star}</span>
                ))}
              </div>
              <p className="text-gray-700 mb-3">
                "I drive from Highland Park to Paradise for all my BMW service. They're honest, 
                skilled, and save me hundreds compared to Dallas dealerships."
              </p>
              <p className="font-semibold">- Sarah M., Dallas</p>
              <p className="text-sm text-gray-600">2023 BMW X5 Owner</p>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex mb-3">
                {'★★★★★'.split('').map((star, i) => (
                  <span key={i} className="text-yellow-400 text-xl">{star}</span>
                ))}
              </div>
              <p className="text-gray-700 mb-3">
                "Best BMW repair shop near Dallas! Fixed my M3's VANOS issue that two Dallas 
                shops couldn't diagnose. Real BMW experts."
              </p>
              <p className="font-semibold">- Michael T., North Dallas</p>
              <p className="text-sm text-gray-600">2020 BMW M3 Owner</p>
            </div>
        </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Dallas's Premier Independent BMW Repair Shop
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            Save 30% vs Dallas BMW Dealerships • Same Day Service • Factory-Trained Technicians
          </p>
          
          <div className="bg-white/10 backdrop-blur rounded-lg p-8 max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold mb-6">Schedule Your BMW Service Today</h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <a
                href="tel:9403490038"
                className="inline-flex items-center justify-center px-6 py-4 bg-white text-blue-600 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
              >
                <FiPhone className="mr-2" />
                (817) 382-2316
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-4 border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors"
              >
                Schedule Online
              </Link>
            </div>
            
            <div className="text-white">
              <p className="flex items-center justify-center mb-2">
                <FiMapPin className="mr-2" />
                116 W Garza Ave, Paradise, TX 76073
              </p>
              <p className="text-sm">
                30 min from Dallas • 35 min from Richardson • 25 min from Denton
              </p>
              <p className="text-sm mt-2">
                <FiClock className="inline mr-2" />
                Mon-Fri 8AM-5PM • Sat 9AM-3PM
              </p>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default DallasBMWRepair;
